import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { of, startWith, switchMap } from 'rxjs';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { AiVoice, type AiVoiceFamily, VoiceLanguage } from '../../core/interfaces/config.interface';
import { AiVoiceConfigurationForm } from '../../core/forms';
import {
  getDeepgramPreviewUrl,
  getGenderTranslationKey,
  VOICE_LANGUAGE_TO_CODE,
} from '../../core/services/ai-assistant-utils';

interface DisplayVoiceTableRow extends AiVoice {
  genderTranslationKey: string;
  computedPreviewAudioUrl?: string;
  matchesLanguage: boolean;
}

@Component({
  selector: 'ai-voice-selector-table',
  standalone: true,
  imports: [
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    GalaxyFormFieldModule,
    GalaxyBadgeModule,
    AiAssistantI18nModule,
  ],
  templateUrl: './ai-voice-selector-table.component.html',
  styleUrl: './ai-voice-selector-table.component.scss',
})
export class AiVoiceSelectorTableComponent {
  readonly voiceConfigForm = input.required<AiVoiceConfigurationForm>();

  protected readonly selectedLanguage = signal<VoiceLanguage | '' | null>(null);
  protected readonly genderFilter = signal({ masculine: false, feminine: false });
  protected readonly pageIndex = signal(0);
  protected readonly pageSize = 5;
  protected readonly displayedColumns = ['selected', 'preview', 'name', 'recommended', 'gender', 'accent'];
  protected readonly previewingVoice = signal<DisplayVoiceTableRow | null>(null);
  private audioElement: HTMLAudioElement | null = null;

  private readonly voiceFamily = toSignal<AiVoiceFamily | null>(
    toObservable(this.voiceConfigForm).pipe(switchMap((form) => form.voiceFamily$)),
  );

  protected readonly voices = computed(() => {
    const family = this.voiceFamily();
    return this.voiceConfigForm().getVoices(family);
  });

  protected readonly selectedVoice = toSignal(
    toObservable(this.voiceFamily).pipe(
      switchMap((family) => {
        if (!family) return of(null);
        const voiceControl = this.voiceConfigForm().getVoiceControl(family.provider);
        if (!voiceControl) return of(null);
        return voiceControl.valueChanges.pipe(startWith(voiceControl.value));
      }),
    ),
    { initialValue: null },
  );

  protected readonly filteredVoiceRows = computed<AiVoice[]>(() => {
    const rows = this.voices();
    const selectedLang = this.selectedLanguage();
    const { masculine, feminine } = this.genderFilter();

    let filtered = rows;
    if (masculine !== feminine) {
      const gender = masculine ? 'masculine' : 'feminine';
      filtered = rows.filter((row) => row.gender === gender);
    }

    if (selectedLang) {
      filtered = [...filtered].sort((a, b) => {
        const aMatch = a.languages?.includes(selectedLang) ? 0 : 1;
        const bMatch = b.languages?.includes(selectedLang) ? 0 : 1;
        return aMatch - bMatch;
      });
    }

    return filtered;
  });

  protected readonly pagedVoiceRows = computed<DisplayVoiceTableRow[]>(() => {
    const rows = this.filteredVoiceRows();
    const start = this.pageIndex() * this.pageSize;
    const selectedLang = this.selectedLanguage();
    const langCode = selectedLang ? VOICE_LANGUAGE_TO_CODE[selectedLang] : undefined;

    return rows.slice(start, start + this.pageSize).map((row) => ({
      ...row,
      genderTranslationKey: getGenderTranslationKey(row.gender),
      computedPreviewAudioUrl: this.computePreviewAudioUrl(row, langCode),
      matchesLanguage: !selectedLang || !!row.languages?.includes(selectedLang),
    }));
  });

  protected readonly availableLanguages = computed(() => {
    const rows = this.voices();
    const languageSet = new Set<VoiceLanguage>();

    rows.forEach((row) => {
      row.languages?.forEach((lang) => languageSet.add(lang));
    });

    return Object.values(VoiceLanguage).filter((lang) => languageSet.has(lang));
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopAudio());

    effect(() => {
      this.voiceFamily();
      untracked(() => this.reset());
    });

    effect(() => {
      this.selectedVoice();
      untracked(() => {
        if (this.previewingVoice()) {
          this.stopAudio();
        }
      });
    });

    effect(() => {
      const selectedLanguage = this.selectedLanguage();
      const { masculine, feminine } = this.genderFilter();
      untracked(() => {
        if (selectedLanguage || masculine || feminine) {
          this.pageIndex.set(0);
        }
      });
    });
  }

  private reset(): void {
    this.selectedLanguage.set(null);
    this.genderFilter.set({ masculine: false, feminine: false });
    this.navigateToSelectedVoicePage();
  }

  protected onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
  }

  protected toggleGenderFilter(gender: 'masculine' | 'feminine', checked: boolean): void {
    this.genderFilter.update((f) => ({ ...f, [gender]: checked }));
  }

  protected selectVoice(row: AiVoice): void {
    const family = this.voiceFamily();
    if (!family) return;
    const voiceControl = this.voiceConfigForm().getVoiceControl(family.provider);
    if (voiceControl && voiceControl.value !== row.value) {
      voiceControl.setValue(row.value);
      voiceControl.markAsDirty();
    }
  }

  protected previewVoice(row: DisplayVoiceTableRow): void {
    const isCurrentlyPlayingThisVoice = this.previewingVoice()?.value === row.value;

    this.stopAudio();

    if (!isCurrentlyPlayingThisVoice) {
      this.playAudio(row);
    }
  }

  private computePreviewAudioUrl(row: AiVoice, langCode?: string): string | undefined {
    if (row.value.startsWith('aura-')) {
      return getDeepgramPreviewUrl(row.value, langCode);
    }
    return row.previewAudioUrl;
  }

  private navigateToSelectedVoicePage(): void {
    const rows = this.voices();
    const family = this.voiceFamily();
    const voiceControl = family ? this.voiceConfigForm().getVoiceControl(family.provider) : null;
    const currentVoice = voiceControl?.value ?? null;
    if (!currentVoice || rows.length === 0) return;
    const index = rows.findIndex((row) => row.value === currentVoice);
    if (index < 0) return;
    this.pageIndex.set(Math.floor(index / this.pageSize));
  }

  private playAudio(row: DisplayVoiceTableRow): void {
    const previewUrl = row.computedPreviewAudioUrl;
    if (!previewUrl) return;

    this.audioElement = new Audio(previewUrl);
    this.audioElement.onended = () => this.previewingVoice.set(null);
    this.audioElement
      .play()
      .then(() => this.previewingVoice.set(row))
      .catch((error) => console.error('Error playing audio:', error));
  }

  private stopAudio(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.audioElement.onended = null;
      this.audioElement = null;
    }
    this.previewingVoice.set(null);
  }
}
